'use client'

import { useEffect, useState, useCallback, useRef } from 'react'
import { VO_DURATIONS_MS } from '@/lib/vo-manifest'

const AMBIENT_TRACK = '/sounds/ambient-lobby.mp3'
const SCREAM_TRACK  = '/sounds/infected-win.mp3'

const GATE_VIDEO   = '/videos/zombie-clip-1.mp4'
const GATE_POSTER  = '/images/gate-poster.webp'
const FINALE_VIDEO = '/videos/zombie-clip-2.mp4'

const BG_FRAMES = [
  '/images/bg-horror.webp',
  '/images/bg-zombie-portrait.webp',
  '/images/bg-patient-zero.webp',
  '/images/bg-game.webp',
] as const

// ── Story — three acts. Acts 1 & 2 fade+collapse when the next act begins. ───
type Act = 1 | 2 | 3
interface StoryLine { text: string; act: Act }

/**
 * The cold open, as a transmission from a containment commander.
 *
 * The speaker is infected and has not turned yet. That is not decoration: it
 * teaches the game's central rule before the player ever sits down — someone
 * can sound completely in command and already be carrying it. The tell is the
 * last fragment of the last line (see TELL_MARK), delivered after the orders,
 * never explained.
 *
 * ⚠ Nine lines in a 3/3/3 act split. Several effects below are keyed to
 * POSITION, not content — the scream fires on `activeLine === 2` as the Act I
 * climax, and act boundaries drive the fade/collapse — so adding, removing or
 * re-grouping lines silently desynchronises the audio from the text. Keep the
 * shape, change the words.
 */
const STORY: StoryLine[] = [
  // Act I — the log
  { text: 'Sector Seven Command. If you hear this, you are inside.', act: 1 },
  { text: 'Day 3 — we sealed the district. Two patients. High fever.', act: 1 },
  { text: 'Day 7 — the hospitals stopped counting.', act: 1 },
  // Act II — the lie
  { text: 'We declared full containment. I signed that order myself.', act: 2 },
  { text: 'We lied.', act: 2 },
  { text: 'It was already past the walls. It came wearing our faces.', act: 2 },
  // Act III — the brief (persists, never fades)
  { text: 'Someone in your room carried it in. They look fine.', act: 3 },
  { text: 'One of you is Patient Zero.', act: 3 },
  { text: 'Trust no one. Find them first. …it is so cold in here.', act: 3 },
]

/** Everything from this marker in the final line is the speaker's tell — it is
 *  rendered as the transmission coming apart rather than as more narration. */
const TELL_MARK = '…'

/**
 * Per-line voiceover.
 *
 * 🔊 ON/OFF IS THE MANIFEST. `VO_DURATIONS_MS` is generated by
 * scripts/generate-vo.py alongside the clips themselves, so an empty array means
 * there is no audio to play and none is requested. There is no separate flag to
 * forget to flip, and no way to ship a flag that disagrees with the files.
 *
 * 🚨 THE CAPTIONS PACE THEMSELVES TO THE VOICE, NOT THE OTHER WAY AROUND.
 * MS_PER_CHAR types at roughly 227 wpm; a composed, grim read lands nearer 130.
 * Every line therefore finished typing well before the voice finished saying it,
 * and the voice ran on over the following caption. Rather than guess a slower
 * fixed rate, each line is typed across its OWN measured clip duration — so text
 * and voice land together whatever read the recording turns out to be.
 *
 * Asset contract, matching the constraints the rest of the splash already obeys:
 *  - Mono, ~56 kbps, band-limited to radio. The whole set is ~170 KB, in line
 *    with the existing tracks in public/sounds (124–679 KB each).
 *  - Radio-degraded and faceless. No name, no portrait — nothing that would have
 *    to be cast, animated, or re-recorded to change.
 *  - The captions are the FLOOR, not a fallback: most mobile sessions are muted
 *    and MiniPay's in-app browser is a muted-by-default context, so the text has
 *    to carry the whole story alone. It already does.
 *
 * Deliberately NOT preloaded — each clip is constructed when its line begins.
 * The splash is where LCP is measured, and pulling the set into the critical
 * path to serve a beat that only starts after a click would regress the
 * PageSpeed numbers tracked in the submission docs.
 */
const VO_AVAILABLE: boolean = VO_DURATIONS_MS.length >= STORY.length
const voSrc = (line: number) => `/sounds/vo/line-${line + 1}.mp3`

/**
 * How long line `i` should take to type.
 *
 * Falls back to the fixed rate whenever there is no voice to match. Clamped so a
 * pathological clip — a botched render, a long silence — cannot leave a caption
 * crawling or flashing past unreadably.
 */
function typingMsFor(i: number, text: string): number {
  const base = text.length * MS_PER_CHAR
  if (!VO_AVAILABLE) return base
  const clip = VO_DURATIONS_MS[i]
  if (!clip || clip <= 0) return base
  return Math.min(base * 3, Math.max(base, clip))
}
const MS_PER_CHAR      = 44
const MS_BETWEEN_LINES = 720
const ACT_FADE_MS      = 1300  // CSS transition duration for fading acts out
const ACT_PAUSE_MS     = 2000  // total pause (fade + settle) before next act types

// ── Per-line render state ─────────────────────────────────────────────────────
interface LineState {
  text:      string
  full:      string
  opacity:   number
  collapsed: boolean
  act:       1 | 2 | 3
}

// ── Pure state-updater factories ──────────────────────────────────────────────
function fadeAct(act: Act) {
  return (prev: LineState[]) => prev.map(l => l.act === act ? { ...l, opacity: 0 } : l)
}
function collapseAct(act: Act) {
  return (prev: LineState[]) => prev.map(l => l.act === act ? { ...l, collapsed: true } : l)
}
function startLine(i: number, text: string, act: Act) {
  return (prev: LineState[]) => {
    const next = [...prev]
    next[i] = { text: '', full: text, opacity: 1, collapsed: false, act }
    return next
  }
}
function updateChar(i: number, text: string, c: number) {
  return (prev: LineState[]) =>
    prev.map((l, idx) => idx === i ? { ...l, text: text.slice(0, c) } : l)
}
function completeLine(i: number, text: string) {
  return (prev: LineState[]) =>
    prev.map((l, idx) => idx === i ? { ...l, text } : l)
}

// ── Async typewriter — only runs once active is true ──────────────────────────
function useStoryTypewriter(active: boolean) {
  const [lines, setLines]           = useState<LineState[]>([])
  const [activeLine, setActiveLine] = useState(0)
  const [done, setDone]             = useState(false)

  useEffect(() => {
    if (!active) return
    setLines([])
    setActiveLine(0)
    setDone(false)

    let cancelled = false
    const wait = (ms: number) => new Promise<void>(r => setTimeout(r, ms))

    async function transitionAct(fadingAct: Act) {
      setLines(fadeAct(fadingAct))
      setTimeout(() => { if (!cancelled) setLines(collapseAct(fadingAct)) }, ACT_FADE_MS + 100)
      await wait(ACT_PAUSE_MS)
    }

    async function typeLine(i: number, text: string, act: Act) {
      setActiveLine(i)
      setLines(startLine(i, text, act))
      // Per-character delay is derived from this line's own budget, so a line
      // with a voiceover types across exactly as long as the voice takes to say
      // it and the two finish together. Without audio this resolves to the flat
      // MS_PER_CHAR the splash has always used.
      const perChar = typingMsFor(i, text) / Math.max(1, text.length)
      await wait(perChar)
      for (let c = 1; c <= text.length; c++) {
        if (cancelled) return
        setLines(updateChar(i, text, c))
        if (c < text.length) await wait(perChar)
      }
      if (!cancelled) setLines(completeLine(i, text))
      await wait(MS_BETWEEN_LINES)
    }

    async function run() {
      let prevAct: number | null = null
      for (let i = 0; i < STORY.length; i++) {
        if (cancelled) return
        const { text, act } = STORY[i]
        if (prevAct !== null && act !== prevAct) {
          await transitionAct(prevAct as Act)
          if (cancelled) return
        }
        await typeLine(i, text, act)
        prevAct = act
      }
      if (!cancelled) setDone(true)
    }

    run()
    return () => { cancelled = true }
  }, [active])

  return { lines, activeLine, done }
}

// ── Connection-aware gating ───────────────────────────────────────────────────
/**
 * True when the ~1 MB gate clip should never be fetched.
 *
 * `saveData` is the user explicitly asking for less traffic. `effectiveType`
 * is Chrome's own estimate from observed RTT/throughput, so it catches a
 * genuinely slow link even when the radio claims 4G — which is the common case
 * on the mid-range Android hardware MiniPay runs on. Absent the API (Safari,
 * Firefox) we assume a good connection and behave as before.
 */
function shouldSkipGateClip(): boolean {
  if (typeof navigator === 'undefined') return false
  const c = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string }
  }).connection
  if (!c) return false
  if (c.saveData) return true
  return ['slow-2g', '2g', '3g'].includes(c.effectiveType ?? '')
}

// ── Particles ─────────────────────────────────────────────────────────────────
interface Particle { id: number; x: number; size: number; delay: number; duration: number }

const PARTICLES: Particle[] = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  size: 3 + Math.random() * 5,
  delay: Math.random() * 8,
  duration: 6 + Math.random() * 8,
}))

// ── Component ─────────────────────────────────────────────────────────────────
export function SplashScreen({ onResolved }: { onResolved?: () => void } = {}) {
  const [visible,      setVisible]      = useState(false)
  const [exiting,      setExiting]      = useState(false)
  const [finaleStatic, setFinaleStatic] = useState(false)
  const [titleSlam,    setTitleSlam]    = useState(false)
  const [splashMuted,  setSplashMuted]  = useState(false)
  const [bgIndex,      setBgIndex]      = useState(0)
  // The gate clip is ~1 MB of decorative loop. `autoPlay` makes the browser
  // fetch it during initial load no matter what `preload` says, so it competed
  // with everything that actually matters on a phone. Mount it only once the
  // page has settled; GATE_POSTER holds the frame until then, so there is no
  // visible gap. See the payload notes in docs/CELO_STARTUPBANK_SUBMISSION.md.
  const [videoReady,   setVideoReady]   = useState(false)
  // gate = logo screen requiring a click; story = typewriter + audio running
  const [phase,        setPhase]        = useState<'gate' | 'story'>('gate')

  const ambientRef         = useRef<HTMLAudioElement | null>(null)
  const transientAudiosRef = useRef<HTMLAudioElement[]>([])
  const lastHeartbeatRef   = useRef(0)

  // Track and play a one-shot Audio so the mute toggle can stop it later.
  const playOneShot = useCallback((src: string, volume: number) => {
    const audio = new Audio(src)
    audio.volume = volume
    transientAudiosRef.current.push(audio)
    audio.addEventListener('ended', () => {
      transientAudiosRef.current = transientAudiosRef.current.filter(a => a !== audio)
    })
    audio.play().catch(() => {})
    return audio
  }, [])

  // Voiceover elements pre-created inside the gate gesture (see beginStory).
  const voPoolRef = useRef<HTMLAudioElement[]>([])

  const stopAllTransients = useCallback(() => {
    const audios = transientAudiosRef.current
    transientAudiosRef.current = []
    for (const audio of audios) audio.pause()
    for (const clip of voPoolRef.current) clip.pause()
  }, [])

  /**
   * Play voiceover line `i` from the pool unlocked at the gate.
   *
   * Falls back to a fresh element if the pool is empty — desktop browsers do
   * not need the unlock, and a muted reader who unmutes mid-story never built
   * one. On mobile that fallback is the silent path the pool exists to avoid,
   * which is why it is a fallback and not the default.
   */
  const playVoLine = useCallback((i: number, volume: number) => {
    // Exactly one line speaks at a time. The pool's clips live outside
    // transientAudiosRef, so stopAllTransients does not clamp them the way it
    // clamped the old per-line elements — silence the rest here instead.
    for (const [j, clip] of voPoolRef.current.entries()) {
      if (j === i) continue
      clip.pause()
      clip.currentTime = 0
    }
    const pooled = voPoolRef.current[i]
    if (pooled) {
      pooled.currentTime = 0
      pooled.volume = volume
      pooled.play().catch(() => {})
      return pooled
    }
    return playOneShot(voSrc(i), volume)
  }, [playOneShot])

  const { lines, activeLine, done } = useStoryTypewriter(phase === 'story')

  // Act III triggers the finale zombie clip — fades in behind the climax text
  const inAct3 = phase === 'story' && (lines[activeLine]?.act === 3 || done)

  // ── First-time visitor ───────────────────────────────────────────────────────
  useEffect(() => {
    const seen = globalThis.window !== undefined && sessionStorage.getItem('plague_intro_seen')
    if (!seen) {
      setVisible(true)
    } else {
      onResolved?.()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Lock body scroll while splash is open ────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = visible ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [visible])

  // ── Defer the gate clip until everything critical has loaded ─────────────────
  // Keyed off the window `load` event rather than requestIdleCallback: idle can
  // fire while the network is still saturated, which put ~1 MB of video in front
  // of the image the page's LCP depends on. `load` is self-tuning — it fires
  // almost immediately on a fast connection and genuinely waits on a slow one.
  useEffect(() => {
    if (!visible) return
    // Skip the clip outright when the connection says not to. It is ~1 MB of
    // pure decoration and the poster frame underneath is visually almost
    // identical, so on a metered or slow link it is the single easiest MB to
    // not spend — and this is exactly the MiniPay audience: mid-range Android
    // on mobile data. Data Saver is an explicit user request; honour it.
    if (shouldSkipGateClip()) return
    let timer: ReturnType<typeof setTimeout>
    const arm = () => { timer = setTimeout(() => setVideoReady(true), 600) }
    if (document.readyState === 'complete') { arm(); return () => clearTimeout(timer) }
    globalThis.addEventListener('load', arm, { once: true })
    return () => { globalThis.removeEventListener('load', arm); clearTimeout(timer) }
  }, [visible])

  // ── Background crossfade — independent 4.5 s timer ───────────────────────────
  useEffect(() => {
    if (!visible) return
    setBgIndex(0)
    const id = setInterval(() =>
      setBgIndex(i => Math.min(BG_FRAMES.length - 1, i + 1)), 4500)
    return () => clearInterval(id)
  }, [visible])

  // ── Gate click — starts audio synchronously inside the user gesture ──────────
  const beginStory = useCallback(() => {
    if (phase !== 'gate') return
    if (!splashMuted) {
      const audio = new Audio(AMBIENT_TRACK)
      audio.loop   = true
      audio.volume = 0.28
      ambientRef.current = audio
      audio.play().catch(() => {})

      // Unlock every voiceover clip HERE, inside the gesture.
      //
      // Mobile Safari grants playback per element, and only to elements
      // created while a user gesture is live. `new Audio(src)` at the moment a
      // line starts typing is seconds too late and is rejected silently by the
      // `.catch(() => {})` in playOneShot — so on a first visit only line 1
      // spoke. Each visit then cached one more clip, letting it start fast
      // enough to squeak through, which is why the story gained a line per
      // reload rather than failing outright.
      //
      // So: build all nine now, start each one muted, and pause it. That
      // play() call is what actually confers permission; from then on the
      // element is unlocked for the rest of the session and the story can
      // play it whenever the line arrives.
      // Each clip is played muted and paused again immediately. Pausing
      // SYNCHRONOUSLY — rather than in the play() promise's .then() — matters:
      // those nine promises resolve at unpredictable moments, and a late one
      // landing after the story had begun would pause a line mid-sentence or
      // unmute a clip that was still running its unlock playback, so several
      // voices talked over each other. pause() is synchronous and the
      // permission is already granted by the play() call itself, so there is
      // nothing to wait for.
      voPoolRef.current = VO_DURATIONS_MS.map((_, i) => {
        const clip = new Audio(voSrc(i))
        clip.preload = 'auto'
        clip.muted = true
        clip.play().catch(() => {})
        clip.pause()
        clip.currentTime = 0
        clip.muted = false
        return clip
      })
    }
    setPhase('story')
  }, [phase, splashMuted])

  // ── Cleanup ambient + transients on visible reset ───────────────────────────
  useEffect(() => {
    if (visible) return
    ambientRef.current?.pause()
    if (ambientRef.current) ambientRef.current.currentTime = 0
    ambientRef.current = null
    stopAllTransients()
    // Drop the unlocked pool too: the next gate click builds a fresh one
    // inside its own gesture, which is the only way it gets permission again.
    voPoolRef.current = []
  }, [visible, stopAllTransients])

  // ── Respond to mute toggle on existing ambient + one-shot audio ──────────────
  useEffect(() => {
    if (splashMuted) {
      ambientRef.current?.pause()
      stopAllTransients()
      return
    }
    const audio = ambientRef.current
    if (!audio) return
    audio.volume = 0.28
    audio.play().catch(() => {})
  }, [splashMuted, stopAllTransients])

  // ── Heartbeat — rate-limited to max once per 1.4 s ───────────────────────────
  useEffect(() => {
    if (!visible || splashMuted || phase !== 'story') return
    const now = Date.now()
    if (now - lastHeartbeatRef.current < 1400) return
    lastHeartbeatRef.current = now
    playOneShot('/sounds/heartbeat.mp3', 0.18)
  }, [activeLine, visible, splashMuted, phase, playOneShot])

  // ── Voiceover — one clip per line, cued as that line starts typing ──────────
  // Per-line rather than one track over the whole intro: the typewriter is
  // self-paced and skippable, so a single welded track would drift out of sync
  // the first time a line ran long and would keep talking over a skip.
  useEffect(() => {
    if (!VO_AVAILABLE || !visible || splashMuted || phase !== 'story') return
    const vo = playVoLine(activeLine, 0.55)
    // Stop this line's voice if the reader skips or mutes mid-sentence;
    // stopAllTransients covers the global cases, this covers line changes.
    return () => { vo.pause() }
  }, [activeLine, visible, splashMuted, phase, playVoLine])

  // ── Scream — fires at Act I climax (line 2 "Day 7…"), echoes out over 4 s ────
  useEffect(() => {
    if (!visible || activeLine !== 2 || splashMuted || phase !== 'story') return
    let fade: ReturnType<typeof setInterval> | null = null
    const fire = setTimeout(() => {
      const scream = playOneShot(SCREAM_TRACK, 0.24)
      let step = 0
      fade = setInterval(() => {
        step++
        scream.volume = Math.max(0, 0.24 * (1 - step / 20))
        if (step >= 20) { if (fade) clearInterval(fade); scream.pause() }
      }, 200)
    }, 500)
    return () => {
      clearTimeout(fire)
      if (fade) clearInterval(fade)
    }
  }, [activeLine, visible, splashMuted, phase, playOneShot])

  // ── Skip entire intro immediately ───────────────────────────────────────────
  const skipAll = useCallback(() => {
    if (globalThis.window !== undefined) sessionStorage.setItem('plague_intro_seen', '1')
    ambientRef.current?.pause()
    if (ambientRef.current) ambientRef.current.currentTime = 0
    ambientRef.current = null
    stopAllTransients()
    setVisible(false)
    onResolved?.()
  }, [stopAllTransients, onResolved])

  // ── Dismiss / finale ─────────────────────────────────────────────────────────
  const dismiss = useCallback(() => {
    if (exiting || finaleStatic) return
    if (globalThis.window !== undefined) sessionStorage.setItem('plague_intro_seen', '1')
    setFinaleStatic(true)
    setTitleSlam(true)
    if (!splashMuted) {
      playOneShot('/sounds/reveal-sting.mp3', 0.35)
    }
    setTimeout(() => setExiting(true), 260)
    if (ambientRef.current) { ambientRef.current.pause(); ambientRef.current.currentTime = 0 }
    setTimeout(() => {
      stopAllTransients()
      setVisible(false); setFinaleStatic(false); setTitleSlam(false); onResolved?.()
    }, 900)
  }, [exiting, finaleStatic, splashMuted, playOneShot, stopAllTransients, onResolved])

  // ── Keyboard dismiss (only during story) ─────────────────────────────────────
  useEffect(() => {
    if (!visible || phase !== 'story') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') dismiss()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [visible, dismiss, phase])

  if (!visible) return null

  return (
    <dialog
      open
      aria-label="Zombie Plague intro"
      style={{
        position:        'fixed',
        inset:           0,
        width:           '100vw',
        height:          '100vh',
        maxWidth:        '100vw',
        maxHeight:       '100vh',
        zIndex:          9999,
        display:         'flex',
        flexDirection:   'column',
        alignItems:      'center',
        justifyContent:  'center',
        backgroundColor: '#000',
        overflow:        'hidden',
        border:          'none',
        padding:         0,
        margin:          0,
        animation: exiting
          ? 'splash-exit 0.8s ease-in forwards'
          : 'splash-enter 0.6s ease-out both',
      }}
    >
      {/* Mute toggle — only shown once story is running */}
      {phase === 'story' && (
        <button
          onClick={() => setSplashMuted(m => !m)}
          aria-label={splashMuted ? 'Unmute intro audio' : 'Mute intro audio'}
          style={{
            position:        'absolute',
            top:             '1rem',
            right:           '1rem',
            zIndex:          10,
            display:         'flex',
            alignItems:      'center',
            gap:             '0.4rem',
            background:      'rgba(6,11,6,0.7)',
            border:          '1px solid rgba(107,142,35,0.35)',
            borderRadius:    '6px',
            padding:         '0.4rem 0.75rem',
            cursor:          'pointer',
            fontFamily:      'var(--font-mono)',
            fontSize:        '0.65rem',
            letterSpacing:   '0.14em',
            color:           splashMuted ? '#7d9a72' : '#6b8e23',
            backdropFilter:  'blur(8px)',
          }}
        >
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>{splashMuted ? '🔇' : '🔊'}</span>
          <span>{splashMuted ? 'MUTED' : 'SOUND ON'}</span>
        </button>
      )}

      {/* Skip button — always visible in both phases */}
      <button
        onClick={skipAll}
        aria-label="Skip intro"
        style={{
          position:      'absolute',
          top:           '1rem',
          left:          '1rem',
          zIndex:        10,
          display:       'flex',
          alignItems:    'center',
          gap:           '0.35rem',
          background:    'rgba(6,11,6,0.7)',
          border:        '1px solid rgba(212,201,178,0.25)',
          borderRadius:  '6px',
          padding:       '0.4rem 0.75rem',
          cursor:        'pointer',
          fontFamily:    'var(--font-mono)',
          fontSize:      '0.65rem',
          letterSpacing: '0.14em',
          color:         'rgba(212,201,178,0.7)',
          backdropFilter: 'blur(8px)',
          transition:    'color 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.color = '#d4c9b2';
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(212,201,178,0.5)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.color = 'rgba(212,201,178,0.7)';
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(212,201,178,0.25)';
        }}
      >
        <span>SKIP</span>
        <span style={{ fontSize: '0.75rem', lineHeight: 1 }}>››</span>
      </button>

      {/* Background layers — stacked, crossfade via opacity transition.
          Only the current frame and the next one are mounted: rendering all
          four up front made the browser fetch every background image during
          initial load, for art the viewer would not see for another 4.5-13.5s.
          The +1 lookahead keeps the next frame decoded before its crossfade.

          Gated on the story phase. These sit BEHIND the gate's poster/video, so
          during the gate they are invisible — yet mounting them there still cost
          ~385 KB (bg-horror 148 KB + bg-zombie-portrait 237 KB) of contention on
          the initial load. On Slow 4G that starved the hero image the page's LCP
          depends on: a preloaded 31 KB file was not finishing until 7.7s, and
          mobile Performance on `/` dropped to 71. */}
      {phase === 'story' && (
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        {BG_FRAMES.filter((_, i) => i <= bgIndex + 1).map((src, i) => (
          <div key={src} style={{
            position:           'absolute',
            inset:              0,
            backgroundImage:    `url(${src})`,
            backgroundSize:     'cover',
            backgroundPosition: 'center',
            transform:          'scale(1.04)',
            filter:             'saturate(0.9) contrast(1.05) brightness(0.38)',
            opacity:            i === bgIndex ? 1 : 0,
            transition:         'opacity 2.5s ease',
          }} />
        ))}
      </div>
      )}

      {/* Poster frame — holds the gate's look while the clip is deferred. */}
      {phase === 'gate' && !videoReady && (
        <div
          aria-hidden="true"
          style={{
            position:           'absolute',
            inset:              0,
            zIndex:             0,
            backgroundImage:    `url(${GATE_POSTER})`,
            backgroundSize:     'cover',
            backgroundPosition: 'center',
            filter:             'saturate(0.7) contrast(1.1) brightness(0.5)',
            opacity:            0.85,
          }}
        />
      )}

      {/* Zombie video — pre-splash gate */}
      {phase === 'gate' && videoReady && (
        <video
          src={GATE_VIDEO}
          poster={GATE_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          style={{
            position:      'absolute',
            inset:         0,
            zIndex:        0,
            width:         '100%',
            height:        '100%',
            objectFit:     'cover',
            filter:        'saturate(0.7) contrast(1.1) brightness(0.5)',
            opacity:       0.85,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Zombie video — Act III climax (fades in over the image stack) */}
      {phase === 'story' && (
        <video
          src={FINALE_VIDEO}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
          style={{
            position:    'absolute',
            inset:       0,
            zIndex:      0,
            width:       '100%',
            height:      '100%',
            objectFit:   'cover',
            filter:      'saturate(0.7) contrast(1.15) brightness(0.42)',
            opacity:     inAct3 ? 0.7 : 0,
            transition:  'opacity 1.6s ease',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Scanlines */}
      <div style={{
        position:        'absolute',
        inset:           0,
        zIndex:          1,
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.18) 2px, rgba(0,0,0,0.18) 4px)',
        pointerEvents:   'none',
      }} />

      {/* Finale static burst */}
      <div style={{
        position:        'absolute',
        inset:           0,
        zIndex:          3,
        pointerEvents:   'none',
        opacity:         finaleStatic ? 0.55 : 0,
        transition:      'opacity 0.18s ease',
        backgroundImage: 'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.38) 0 1px, transparent 1px), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.28) 0 1px, transparent 1px), radial-gradient(circle at 55% 30%, rgba(255,255,255,0.42) 0 1px, transparent 1px)',
        backgroundSize:  '6px 6px, 8px 8px, 5px 5px',
        animation:       finaleStatic ? 'splash-static 0.22s steps(4) infinite' : 'none',
      }} />

      {/* Floating particles */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        {PARTICLES.map(p => (
          <span key={p.id} style={{
            position:        'absolute',
            bottom:          '-12px',
            left:            `${p.x}%`,
            width:           `${p.size}px`,
            height:          `${p.size}px`,
            borderRadius:    '50%',
            backgroundColor: 'rgba(107,142,35,0.35)',
            boxShadow:       '0 0 6px rgba(107,142,35,0.5)',
            animation:       `splash-particle ${p.duration}s ${p.delay}s ease-in infinite`,
          }} />
        ))}
      </div>

      {/* Blood vignette */}
      <div style={{
        position:      'absolute',
        inset:         0,
        zIndex:        1,
        pointerEvents: 'none',
        background:    'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.7) 100%), radial-gradient(ellipse at center, transparent 35%, rgba(180,0,0,0.22) 100%)',
      }} />

      {/* ── Gate phase — click to unlock audio + begin story ─────────────────── */}
      {phase === 'gate' && (
        <div style={{
          position:      'relative',
          zIndex:        2,
          display:       'flex',
          flexDirection: 'column',
          alignItems:    'center',
          gap:           '1.5rem',
          padding:       '1rem',
          textAlign:     'center',
        }}>
          {/* LCP anchor. Every other visual on this screen is `inset: 0`, and
              Chrome excludes full-viewport images from Largest Contentful Paint
              as backgrounds — so before this existed the gate offered no LCP
              candidate at all and Lighthouse returned NO_LCP, which voids the
              entire Performance score. This img is deliberately bounded (not
              full-bleed) and its animation touches transform only: an ancestor
              or self opacity of 0 would make it ineligible again. */}
          <img
            src="/images/splash-mark.webp"
            alt=""
            width={224}
            height={224}
            fetchPriority="high"
            decoding="sync"
            style={{
              width:     'clamp(7rem, 30vw, 14rem)',
              height:    'auto',
              animation: 'splash-mark-pulse 3s ease-in-out infinite',
              filter:    'drop-shadow(0 0 24px rgba(230,51,41,0.8))',
            }}
          />

          <h1 style={{
            fontFamily:    'var(--font-display)',
            fontSize:      'clamp(2.4rem, 7vw, 4.2rem)',
            lineHeight:    1,
            color:         '#d4c9b2',
            letterSpacing: '0.06em',
            textShadow:    '0 0 30px rgba(230,51,41,0.5)',
            margin:        0,
          }}>
            ZOMBIE PLAGUE
          </h1>

          {/* 🚨 THE LABEL HAS TO DESCRIBE WHAT THE BUTTON DOES.
              "DARE TO ENTER" promised the game and delivered a cutscene, so the
              first thing it did was break its own promise — and it framed the
              splash as a dare, which is a different tone from the one the
              transmission actually has. The speaker names themselves in the
              first line ("Sector Seven Command"), so the button borrows their
              words. It also sets up the reveal: this is a message from the
              officer who signed the containment order, and by the last line you
              learn what happened to them.

              Sized with clamps and nowrap because it is 20 characters against
              13 — at a fixed 1rem and 0.28em of tracking it overflowed a 375px
              phone. */}
          <button
            onClick={beginStory}
            style={{
              marginTop:       '0.5rem',
              padding:         'clamp(0.7rem, 2.4vw, 0.75rem) clamp(1.1rem, 6vw, 2.5rem)',
              fontFamily:      'var(--font-mono)',
              fontSize:        'clamp(0.72rem, 3vw, 1rem)',
              letterSpacing:   '0.2em',
              whiteSpace:      'nowrap',
              textTransform:   'uppercase',
              fontWeight:      700,
              color:           '#060b06',
              backgroundColor: '#6b8e23',
              border:          '2px solid #6b8e23',
              borderRadius:    '6px',
              cursor:          'pointer',
              boxShadow:       '0 0 24px rgba(107,142,35,0.6)',
              animation:       'splash-pulse 2s ease-in-out infinite',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 40px rgba(107,142,35,0.9)'
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 24px rgba(107,142,35,0.6)'
            }}
          >
            Message from Command
          </button>

          <p style={{
            fontFamily:    'var(--font-mono)',
            fontSize:      '0.62rem',
            letterSpacing: '0.14em',
            color:         '#7d9a72',
            margin:        0,
          }}>
            {/* CLICK TO BEGIN · AUDIO ENABLED */}
          </p>
        </div>
      )}

      {/* ── Story phase — typewriter + enter button ───────────────────────────── */}
      {phase === 'story' && (
        <div style={{
          position:      'relative',
          zIndex:        2,
          display:       'flex',
          flexDirection: 'column',
          alignItems:    'center',
          gap:           '1.25rem',
          padding:       '1rem',
          maxWidth:      '600px',
          width:         '100%',
          textAlign:     'center',
        }}>

          {/* Biohazard + title */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{
              fontSize:  'clamp(2.5rem, 10vw, 4rem)',
              lineHeight: 1,
              animation: 'splash-pulse 3s ease-in-out infinite',
              filter:    'drop-shadow(0 0 24px rgba(230,51,41,0.8))',
            }}>☣</div>

            <h1 style={{
              fontFamily:    'var(--font-display)',
              fontSize:      'clamp(2.4rem, 7vw, 4.2rem)',
              lineHeight:    1,
              color:         '#d4c9b2',
              letterSpacing: '0.06em',
              textShadow:    '0 0 30px rgba(230,51,41,0.5)',
              margin:        0,
              transform:     titleSlam ? 'scale(1.12) skewX(-2deg)' : 'scale(1)',
              transition:    'transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1)',
            }}>
              ZOMBIE PLAGUE
            </h1>
          </div>

          {/* Story lines — fade+collapse between acts */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {lines.map((line, i) => {
              const isAct3   = line.act === 3
              const isClimaxLine = i === STORY.length - 1
              const isClimax = isAct3 && isClimaxLine && done
              // 16px reading floor (project type-scale policy) — story text is
              // body copy, not decoration.
              let fontSize = '1rem'
              if (isAct3) fontSize = i === STORY.length - 1 ? '1.3rem' : '1.1rem'
              let marginBottom: string | number = isAct3 ? '0.55rem' : '0.32rem'
              if (line.collapsed) marginBottom = 0
              let textColor = '#7a8c74'
              if (isAct3) textColor = '#d4c9b2'
              if (isClimax) textColor = '#e63329'
              const cursor = i === activeLine && !done
                ? { borderRight: '2px solid #6b8e23', paddingRight: '4px' }
                : {}
              return (
                <div key={line.full} style={{
                  opacity:      line.opacity,
                  // 4.5rem fits ~2.5 wrapped lines at the 1.1–1.3rem Act III
                  // sizes, so a line that wraps to three — "One of you is
                  // Patient Zero." on a narrow viewport — lost its last word to
                  // `overflow: hidden`. The cap exists only to animate the
                  // collapse between acts, so it now applies only while
                  // collapsing; an open line is bounded by its own content.
                  maxHeight:    line.collapsed ? '0px' : '20rem',
                  overflow:     'hidden',
                  transition:   `opacity ${ACT_FADE_MS}ms ease, max-height ${ACT_FADE_MS + 200}ms ease, margin-bottom ${ACT_FADE_MS + 200}ms ease`,
                  marginBottom,
                  width:        '100%',
                  textAlign:    'center',
                }}>
                  <p style={{
                    margin:        0,
                    fontFamily:    'var(--font-mono)',
                    fontSize,
                    lineHeight:    1.45,
                    letterSpacing: isAct3 ? '0.04em' : '0.06em',
                    paddingRight:  '0.12em',
                    color:      textColor,
                    textShadow: isClimax ? '0 0 14px rgba(230,51,41,0.75)' : 'none',
                    fontWeight: isAct3 ? 600 : 400,
                    ...cursor,
                  }}>
                    {/* The tell. On the final line only, the fragment after the
                        ellipsis is rendered as the transmission degrading —
                        dimmer, unsteady, no longer in the commander's voice.
                        Deliberately a VISUAL effect: it has to land for the
                        muted majority, who will never hear the performance. */}
                    {isClimaxLine && line.text.includes(TELL_MARK) ? (
                      <>
                        {line.text.slice(0, line.text.indexOf(TELL_MARK))}
                        <span style={{
                          color:      '#8a6f68',
                          fontStyle:  'italic',
                          fontWeight: 400,
                          textShadow: '0 0 10px rgba(230,51,41,0.35)',
                          animation:  'splash-tell 2.4s ease-in-out infinite',
                        }}>
                          {line.text.slice(line.text.indexOf(TELL_MARK))}
                        </span>
                      </>
                    ) : line.text}
                  </p>
                </div>
              )
            })}
          </div>

          {/* ENTER button — appears when story finishes */}
          <button
            onClick={dismiss}
            disabled={exiting || finaleStatic}
            style={{
              marginTop:     '0.25rem',
              padding:       '0.75rem 2.5rem',
              fontFamily:    'var(--font-mono)',
              fontSize:      '1rem',
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              fontWeight:    700,
              color:         '#060b06',
              backgroundColor: '#6b8e23',
              border:        '2px solid #6b8e23',
              borderRadius:  '6px',
              cursor:        'pointer',
              boxShadow:     '0 0 24px rgba(107,142,35,0.6)',
              opacity:       done ? 1 : 0,
              transform:     done ? 'translateY(0)' : 'translateY(8px)',
              transition:    'opacity 0.6s ease, transform 0.6s ease, box-shadow 0.2s',
              pointerEvents: done && !exiting && !finaleStatic ? 'auto' : 'none',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 40px rgba(107,142,35,0.9)'
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 24px rgba(107,142,35,0.6)'
            }}
          >
            ENTER
          </button>

          <p style={{
            fontFamily:    'var(--font-mono)',
            fontSize:      '0.65rem',
            letterSpacing: '0.15em',
            color:         '#7d9a72',
            margin:        0,
            opacity:       done ? 0.7 : 0,
            transition:    'opacity 0.8s 0.3s',
          }}>
            Press ENTER or SPACE to continue
          </p>

        </div>
      )}
    </dialog>
  )
}
